import React, { useImperativeHandle, useState, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { colorSchemeLightCold, themeQuartz } from 'ag-grid-community';
import { colorPalette } from 'shared/constants/theme';
import { ColumnChooserButton } from './column-chooser';
import { TableActiveFilters } from './active-filters';
import styles from './table.module.scss';
import classNames from 'classnames';

/**
 * @type {import('ag-grid-community').ColDef}
 * `cellClass` vertically-centers cells; a per-column `cellClass` replaces it
 * (not merged), so custom-cellClass columns must re-add flex centering.
 */
const DEFAULT_COL_DEF = {
  resizable: true,
  minWidth: 100,
  cellClass: styles.defaultCell,
};

const EMPTY_OBJ = {};

const themeLightCold = themeQuartz.withPart(colorSchemeLightCold).withParams({
  accentColor: colorPalette.primaryMain5,
  backgroundColor: colorPalette.neutral1,
  wrapperBorder: {
    style: 'solid',
    color: colorPalette.primaryMain3,
    width: 1,
  },

  // Header styles
  headerBackgroundColor: colorPalette.primaryMain2,
  headerCellHoverBackgroundColor: colorPalette.primaryMain3,
  headerColumnBorder: { style: 'solid', color: colorPalette.primaryMain3, width: 1 },
  headerRowBorder: { style: 'solid', color: colorPalette.primaryMain3, width: 1 },
  headerFontWeight: '600',
  headerTextColor: colorPalette.neutral6,
  headerColumnResizeHandleColor: 'transparent',

  // Row styles
  rowHoverColor: colorPalette.primaryMain2_5,
  rowHeight: 44,
  rowBorder: { color: colorPalette.neutral3 },
  oddRowBackgroundColor: colorPalette.neutral2,
});

/**
 * @typedef {Object} ActiveFiltersProps
 * @property {Object} [filterValues] - Current filter values keyed by colId (controlled mode)
 * @property {Function} [onFilterChange] - Called with (colId, newValue) when filter removed
 * @property {Function} [onClearAll] - Called when "Clear all" clicked
 */

/**
 * @typedef {Object} TableContainerProps
 * @property {React.HTMLProps<HTMLDivElement>} [containerProps] - Props to spread on the wrapper div
 * @property {string} [rootClassName] - Additional class for the root container
 * @property {React.Ref<import('ag-grid-react').AgGridReact} [ref] - Ref to expose table API methods
 * @property {import('ag-grid-community').ThemeDefaultParams} [themeOverrides] - Theme overrides for Ag-Grid
 * @property {boolean} [pagination=true] - Enable pagination (default: true). A `paginationPageSize` must be a member of `paginationPageSizeSelector`, or the page-size dropdown renders blank.
 * @property {boolean} [enableColumnChooser=false] - Show gear icon to toggle column visibility
 * @property {boolean} [enableActiveFilters=false] - Show active filter tags above the table
 * @property {ActiveFiltersProps} [activeFiltersProps] - Props for controlled active filters mode
 */

/**
 * Design System Table component wrapping AgGridReact
 * @param {import('ag-grid-react').AgGridReactProps & TableContainerProps} props
 */
export function Table({
  containerProps = {},
  rootClassName = '',
  ref,
  enableColumnChooser = false,
  enableActiveFilters = false,
  activeFiltersProps = {},
  themeOverrides = null,
  pagination = true,
  defaultColDef = EMPTY_OBJ,
  onGridReady,
  ...props
}) {
  const [gridApi, setGridApi] = useState(null);

  useImperativeHandle(
    ref,
    () => ({
      getApi: () => gridApi,
      getDisplayedSelectedRows: () => {
        if (!gridApi) return [];
        const selectedNodes = gridApi.getSelectedNodes();
        return selectedNodes.filter(node => node.displayed).map(node => node.data);
      },
    }),
    [gridApi],
  );

  const mergedColDef = useMemo(() => ({ ...DEFAULT_COL_DEF, ...defaultColDef }), [defaultColDef]);

  const handleGridReady = useCallback(
    event => {
      setGridApi(event.api);
      onGridReady?.(event);
    },
    [onGridReady],
  );

  const theme = themeOverrides ? themeLightCold.withParams(themeOverrides) : themeLightCold;

  return (
    <div className={classNames(styles.root, rootClassName)}>
      {enableActiveFilters && (
        <TableActiveFilters api={gridApi} activeFiltersProps={activeFiltersProps} />
      )}
      <div
        {...containerProps}
        className={classNames(styles.tableContainer, containerProps?.className)}>
        {enableColumnChooser && <ColumnChooserButton api={gridApi} />}
        <AgGridReact
          pagination={pagination}
          theme={theme}
          defaultColDef={mergedColDef}
          enableCellTextSelection
          enableFilterHandlers
          onGridReady={handleGridReady}
          {...props}
        />
      </div>
    </div>
  );
}
